import type { Image } from '../types';

/**
 * Replaces the inner HTML of the Nth <section> in a full MARP document,
 * preserving the section's attributes.
 */
export function patchSectionContent(
  fullHTML: string,
  sectionIndex: number,
  newInnerHTML: string,
): string {
  let count = 0;
  return fullHTML.replace(/<section([^>]*)>([\s\S]*?)<\/section>/g, (match, attrs) => {
    if (count++ === sectionIndex) return `<section${attrs}>${newInnerHTML}</section>`;
    return match;
  });
}

/** Extracts the inner HTML of the Nth <section> from the full MARP document. */
export function extractSectionInnerHTML(fullHTML: string, sectionIndex: number): string {
  let count = 0;
  let found = '';
  fullHTML.replace(/<section[^>]*>([\s\S]*?)<\/section>/g, (_, inner) => {
    if (count++ === sectionIndex) found = inner;
    return '';
  });
  return found;
}

/**
 * Parses the MARP HTML and returns a map of placeholder_id → 1-based slide number.
 * Used in Phase 3 to show "Slide N" labels even for records seeded before SlideNumber
 * was stored in the database (those have slide_number = 0).
 */
export function buildSlideNumberMap(html: string): Map<string, number> {
  if (!html) return new Map();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const sections = Array.from(doc.querySelectorAll('section'));
  const map = new Map<string, number>();
  doc.querySelectorAll('div.img-placeholder[data-id]').forEach((div) => {
    const id = div.getAttribute('data-id');
    const section = div.closest('section');
    if (id && section) map.set(id, sections.indexOf(section) + 1);
  });
  return map;
}

const LAYOUT_CLASS_MAP: Record<string, string> = {
  'img-right': 'slide-img-right',
  'img-left':  'slide-img-left',
  'img-top':   'slide-img-top',
  'img-full':  'slide-img-full',
};
const LAYOUTS = Object.keys(LAYOUT_CLASS_MAP).join('|');

/**
 * Replaces img-placeholder divs with real <img> tags.
 * Only images that have a blob_url are injected; others are left as placeholders.
 */
export function injectImagesIntoHTML(html: string, images: Image[]): string {
  let result = html;
  for (const img of images) {
    if (!img.blob_url) continue;
    const safeId = img.placeholder_id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(
      `<div class="img-placeholder\\s+(${LAYOUTS})"\\s+data-id="${safeId}"[^>]*>[\\s\\S]*?<\\/div>`,
      'gi',
    );
    result = result.replace(re, (_, layout: string) => {
      const cls = LAYOUT_CLASS_MAP[layout.toLowerCase()] ?? 'slide-img-full';
      return `<img src="${img.blob_url}" class="${cls}" alt="${img.placeholder_id}" style="max-width:100%;height:auto;object-fit:contain;" />`;
    });
  }
  return result;
}

/**
 * Wraps MARP HTML for read-only iframe preview.
 * Patches history API (prevents Bespoke.js SecurityError in blob iframes) and
 * optionally navigates to a specific slide index on load.
 */
export function buildPreviewDeckDoc(fullMarpHTML: string, startMarpIndex = 0): string {
  const historyPatch = `<script id="_prev_hist">(function(){
  try{history.replaceState=function(){};history.pushState=function(){};}catch(e){}
}());</script>`;

  const navScript = startMarpIndex > 0
    ? `<script id="_prev_nav">(function(){
  window.addEventListener('load',function(){
    setTimeout(function(){
      for(var i=0;i<${startMarpIndex};i++){
        document.dispatchEvent(new KeyboardEvent('keydown',{key:'ArrowRight',bubbles:true,cancelable:true}));
      }
    },250);
  });
}());</script>`
    : '';

  let html = fullMarpHTML;
  html = html.replace(/(<head[^>]*>)/i, '$1' + historyPatch);
  html = html.includes('</head>')
    ? html.replace('</head>', navScript + '</head>')
    : navScript + html;
  return html;
}

// ── Slide verification ────────────────────────────────────────────────────────

/**
 * Wraps the MARP HTML with a script that navigates through every slide,
 * checks each <img> element (loaded, rendered size, side-of-centre alignment),
 * and any remaining img-placeholder divs, then posts results back to the parent
 * window via postMessage.
 *
 * Messages emitted:
 *   { type: 'slide-verify-progress', result: SlideVerifyResult, checked: N, total: N }
 *   { type: 'slide-verify-complete',  results: SlideVerifyResult[] }
 *
 * The iframe must be rendered at a real size (≥ 960×540) for getBoundingClientRect
 * to return meaningful values.
 */
export function buildVerificationDeckDoc(fullMarpHTML: string): string {
  const historyPatch = `<script id="_vfy_hist">(function(){
  try{history.replaceState=function(){};history.pushState=function(){};}catch(e){}
}());</script>`;

  // Each slide is checked while it is the active (visible) Bespoke.js slide so
  // getBoundingClientRect works correctly. After checking we advance one step
  // and wait 350 ms for the CSS transition before repeating.
  const scanScript = `<script id="_vfy_scan">(function(){
  var results=[];
  window.addEventListener('load',function(){
    var deck=document.getElementById(':$p')||document.body;
    var svgSlides=Array.from(deck.querySelectorAll('svg[data-marpit-svg]'));
    var total=svgSlides.length;
    if(!total){
      try{window.parent.postMessage({type:'slide-verify-complete',results:[]},'*');}catch(e){}
      return;
    }
    function verify(idx){
      var svg=svgSlides[idx];
      var section=svg?svg.querySelector('section'):null;
      var sRect=section?section.getBoundingClientRect():null;
      var imgs=[];
      var unresolved=[];
      if(section){
        section.querySelectorAll('img').forEach(function(img){
          var r=img.getBoundingClientRect();
          var loaded=img.complete&&img.naturalWidth>0;
          var alignOk=true;
          if(sRect&&sRect.width>0&&r.width>0){
            var cx=r.left+r.width/2;
            var mid=sRect.left+sRect.width/2;
            if(img.classList.contains('slide-img-right'))alignOk=cx>mid-10;
            else if(img.classList.contains('slide-img-left'))alignOk=cx<mid+10;
          }
          imgs.push({
            alt:img.alt||'',
            loaded:loaded,
            naturalWidth:img.naturalWidth,
            naturalHeight:img.naturalHeight,
            cssClass:img.className,
            alignmentOk:alignOk,
            renderedWidth:Math.round(r.width),
            renderedHeight:Math.round(r.height)
          });
        });
        section.querySelectorAll('div[data-id]').forEach(function(d){
          unresolved.push(d.getAttribute('data-id')||'');
        });
      }
      var pass=unresolved.length===0&&
        imgs.every(function(i){return i.loaded&&i.alignmentOk&&i.renderedWidth>0;});
      var result={
        marpIndex:idx,
        images:imgs,
        unresolvedPlaceholders:unresolved,
        pass:pass
      };
      results.push(result);
      try{window.parent.postMessage({type:'slide-verify-progress',result:result,checked:idx+1,total:total},'*');}catch(e){}
    }
    var idx=0;
    function step(){
      verify(idx);
      idx++;
      if(idx<total){
        document.dispatchEvent(new KeyboardEvent('keydown',{key:'ArrowRight',bubbles:true,cancelable:true}));
        setTimeout(step,350);
      }else{
        try{window.parent.postMessage({type:'slide-verify-complete',results:results},'*');}catch(e){}
      }
    }
    setTimeout(step,450);
  });
}());</script>`;

  let html = fullMarpHTML;
  html = html.replace(/(<head[^>]*>)/i, '$1' + historyPatch);
  html = html.includes('</head>')
    ? html.replace('</head>', scanScript + '</head>')
    : scanScript + html;
  return html;
}

/**
 * Creates a blob URL for a read-only slide preview with images injected.
 * Returns the URL and a revoke function — call revoke() on unmount.
 */
export function createPreviewBlobUrl(
  fullMarpHTML: string,
  images: Image[],
  startMarpIndex = 0,
): { url: string; revoke: () => void } {
  const injected = injectImagesIntoHTML(fullMarpHTML, images);
  const doc = buildPreviewDeckDoc(injected, startMarpIndex);
  const blob = new Blob([doc], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  return { url, revoke: () => URL.revokeObjectURL(url) };
}
